import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Alert,
  Divider,
} from '@mui/material';
import { PhoneAndroid, Security, Check } from '@mui/icons-material';

const TwilioComplianceMockup: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Security sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            DashboardFox Security
          </Typography>
          <Typography variant="h5" color="text.secondary">
            Two-Factor Authentication Setup
          </Typography>
        </Box>

        {!submitted ? (
          <Box component="form" onSubmit={handleSubmit}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                SMS Verification Required
              </Typography>
              <Typography variant="body2">
                To protect your account, we use SMS-based two-factor authentication. 
                You'll receive a verification code when logging in.
              </Typography>
            </Alert>

            <Box sx={{ mb: 3, p: 3, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneAndroid /> How It Works
              </Typography>
              <Typography variant="body2" paragraph>
                1. <strong>You enable 2FA:</strong> Add your phone number to your account
              </Typography>
              <Typography variant="body2" paragraph>
                2. <strong>Verification codes:</strong> When you log in, we'll send you a 6-digit code via SMS
              </Typography>
              <Typography variant="body2" paragraph>
                3. <strong>Enter the code:</strong> Complete your login by entering the code
              </Typography>
              <Typography variant="body2">
                4. <strong>Stay secure:</strong> Your account is protected even if someone gets your password
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              What We'll Send You
            </Typography>
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1, fontFamily: 'monospace' }}>
              <Typography variant="body2" gutterBottom>
                <strong>Example SMS Message:</strong>
              </Typography>
              <Typography variant="body2">
                "Your DashboardFox verification code is: 123456. Valid for 5 minutes."
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>Message Frequency:</strong> You'll only receive SMS messages when you log in to your account.
            </Typography>

            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>Opt-Out:</strong> You can disable SMS 2FA at any time in your account settings. 
              Message and data rates may apply.
            </Typography>

            <Divider sx={{ my: 3 }} />

            <TextField
              fullWidth
              label="Your Phone Number (for demo)"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+1 (555) 123-4567"
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  I consent to receive SMS verification codes from DashboardFox for 
                  two-factor authentication purposes. I understand I can disable this 
                  feature at any time in my account settings.
                </Typography>
              }
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={!consent || !phoneNumber}
            >
              Enable SMS Two-Factor Authentication
            </Button>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Check sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              SMS 2FA Enabled Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Your account is now protected with SMS two-factor authentication.
            </Typography>
            <Alert severity="success" sx={{ mt: 3 }}>
              You'll receive a verification code via SMS the next time you log in.
            </Alert>
          </Box>
        )}

        <Divider sx={{ my: 4 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Privacy & Security Information
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Your phone number is stored securely and only used for authentication.
            We never share your information with third parties.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default TwilioComplianceMockup;
